import type { ReactNode } from "react";
import { usePageIdentity } from "../editorial/PageIdentityContext";
import { Stack } from "./stack";

interface PageHeaderProps {
  action?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Browser-tab and history name for the page, when the heading is not it.
   * A heading that names a group of routes rather than this one — the shared
   * "Settings" heading standing above every settings tab — would otherwise
   * claim each tab's identity as its own, so such a page names itself here.
   */
  pageTitle?: ReactNode;
}

export function PageHeader({ action, pageTitle, subtitle, title }: PageHeaderProps) {
  usePageIdentity(pageTitle ?? title);
  return (
    <header className="flex min-h-(--control-h-lg) flex-col gap-(--space-4) sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <Stack gap="related">
          <h1
            className="type-h1 text-balance font-display text-(--color-text) [overflow-wrap:anywhere]"
            tabIndex={-1}
          >
            {title}
          </h1>
          {subtitle ? (
            <p className="type-body text-pretty text-(--color-text-secondary)">{subtitle}</p>
          ) : null}
        </Stack>
      </div>
      {action ? (
        <div className="flex max-w-full flex-wrap items-center gap-(--space-2) sm:shrink-0">
          {action}
        </div>
      ) : null}
    </header>
  );
}

interface SectionHeaderProps {
  action?: ReactNode;
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
}

export function SectionHeader({ action, icon, subtitle, title }: SectionHeaderProps) {
  return (
    // Shares PanelHeader's edge register: both label a container in the utility
    // register, so their padding tokens must agree or a panel built from this
    // one sits 4px off the body beneath it and flush against its own top border.
    <div className="flex items-center justify-between gap-(--space-4) border-b border-(--color-border) px-(--panel-header-pad-x) pb-(--panel-header-pad-y-bottom) pt-(--panel-header-pad-y-top)">
      <div className="flex min-w-0 items-center gap-(--space-3)">
        {icon ? <div className="shrink-0">{icon}</div> : null}
        <div className="min-w-0">
          <h2 className="type-panel-title truncate leading-tight text-(--color-text)">{title}</h2>
          {subtitle ? (
            <p className="type-label mt-(--space-1) text-(--color-text-secondary)">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {action ? <div className="flex shrink-0 flex-wrap gap-(--space-3)">{action}</div> : null}
    </div>
  );
}
